/**
 * Email Thread List Component
 *
 * Displays paginated list of email threads with conversation grouping and PDF viewing
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Collapse,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  PlayArrow as ProcessIcon,
  AttachFile as AttachmentIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';

import { format } from 'date-fns';

import { EmailThread, EmailThreadStatus, ThreadListParams } from '../../types/emailProcessor';
import emailProcessorService from '../../services/emailProcessorService';
import { primaryButtonSx } from '../../theme';

interface EmailThreadListProps {
  onError: (error: string) => void;
}

interface ExpandedRowProps {
  thread: EmailThread;
  onProcess: (threadId: number) => void;
  onViewAttachment: (threadId: number, attachmentId: number) => void;
  onViewThread: (thread: EmailThread) => void;
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({ thread, onProcess, onViewAttachment, onViewThread }) => {
  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'failed':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'processing':
        return <CircularProgress size={16} />;
      default:
        return <WarningIcon fontSize="small" color="warning" />;
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default' }}>
      <Grid container spacing={2}>
        {/* Thread Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon fontSize="small" />
            Thread Information
          </Typography>
          <List dense>
            {(thread as any).is_declaration && (
              <ListItem>
                <ListItemText
                  primary="Type"
                  secondary={
                    <Chip
                      label="Declaration"
                      size="small"
                      color="primary"
                      icon={<EmailIcon fontSize="small" />}
                    />
                  }
                />
              </ListItem>
            )}
            {(thread as any).declaration_state && (
              <ListItem>
                <ListItemText
                  primary="Declaration State"
                  secondary={(thread as any).declaration_state}
                />
              </ListItem>
            )}
            {(thread as any).reply_count > 0 && (
              <ListItem>
                <ListItemText
                  primary="Replies"
                  secondary={
                    <Chip
                      label={(thread as any).reply_count}
                      size="small"
                      icon={<ReplyIcon fontSize="small" />}
                    />
                  }
                />
              </ListItem>
            )}
            {(thread as any).error_message && (
              <ListItem>
                <Alert severity="error" sx={{ width: '100%' }}>
                  {(thread as any).error_message}
                </Alert>
              </ListItem>
            )}
          </List>
        </Grid>

        {/* Attachments */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachmentIcon fontSize="small" />
            Attachments ({thread.attachments.length})
          </Typography>

          {thread.attachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No attachments
            </Typography>
          ) : (
            <List dense>
              {thread.attachments.map((attachment) => (
                <ListItem key={attachment.id}>
                  <ListItemIcon>
                    {(attachment as any).content_type?.includes('pdf') ? (
                      <PdfIcon color="error" />
                    ) : (
                      <DescriptionIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={attachment.filename}
                    secondary={
                      <>
                        {emailProcessorService.formatFileSize(attachment.size_bytes)}
                        {(attachment as any).document_type && ` • ${(attachment as any).document_type}`}
                        {(attachment as any).document_type_confidence &&
                          ` (${(attachment as any).document_type_confidence}%)`}
                      </>
                    }
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    {getStatusIcon(attachment.status)}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onViewAttachment(thread.id, attachment.id)}
                      startIcon={<VisibilityIcon />}
                    >
                      View
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Grid>

        {/* Email Body Preview */}
        {thread.body_text && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Email Body Preview
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 150, overflow: 'auto' }}>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {thread.body_text.substring(0, 500)}
                {thread.body_text.length > 500 && '...'}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onViewThread(thread)}
              startIcon={<VisibilityIcon />}
              sx={primaryButtonSx}
            >
              View Full Conversation
            </Button>
            {/* Show Retry button only for failed threads that are NOT declaration emails already submitted */}
            {thread.status === 'failed' &&
             !(thread.is_declaration && (thread.declaration_state === 'submitted' || thread.declaration_state === 'failed')) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ProcessIcon />}
                onClick={() => onProcess(thread.id)}
              >
                Retry Processing
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const EmailThreadList: React.FC<EmailThreadListProps> = ({ onError }) => {

  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<EmailThreadStatus | ''>('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [page, pageSize, statusFilter]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const params: ThreadListParams = {
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await emailProcessorService.getEmailThreads(params);
      setThreads(response.threads);
      setTotal(response.total);
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to load email threads');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessThread = async (threadId: number) => {
    try {
      await emailProcessorService.processEmailThread(threadId);
      loadThreads();
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to process thread');
    }
  };

  const handleViewAttachment = async (threadId: number, attachmentId: number) => {
    try {
      const response = await emailProcessorService.getAttachmentFile(threadId, attachmentId);

      // Create a blob URL and open it in a new tab
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up the URL after opening
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to open attachment');
    }
  };

  const handleViewThread = async (thread: EmailThread) => {
    try {
      // Load full thread details with conversation messages
      const fullThread = await emailProcessorService.getEmailThreadDetails(thread.id);
      setSelectedThread(fullThread);
      setDetailsOpen(true);
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to load thread details');
    }
  };

  const toggleRow = (threadId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(threadId)) {
      newExpanded.delete(threadId);
    } else {
      newExpanded.add(threadId);
    }
    setExpandedRows(newExpanded);
  };

  if (loading && threads.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{"Filter by Status"}</InputLabel>
          <Select
            value={statusFilter}
            label={"Filter by Status"}
            onChange={(e) => setStatusFilter(e.target.value as EmailThreadStatus | '')}
          >
            <MenuItem value="">{"All"}</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="partially_processed">Partially Processed</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={loadThreads} disabled={loading}>
          {"Refresh"}
        </Button>
      </Box>

      {/* Threads Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell width={70}>{"ID"}</TableCell>
              <TableCell>{"Sender"}</TableCell>
              <TableCell>{"Subject"}</TableCell>
              <TableCell>{"Received"}</TableCell>
              <TableCell align="center">{"Attachments"}</TableCell>
              <TableCell>{"Status"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {threads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    {"No email threads found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              threads.map((thread) => (
                <React.Fragment key={thread.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(thread.id)}>
                        {expandedRows.has(thread.id) ? <CollapseIcon /> : <ExpandIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        #{thread.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {thread.sender_name || thread.sender_email}
                      </Typography>
                      {thread.sender_name && (
                        <Typography variant="caption" color="text.secondary">
                          {thread.sender_email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {thread.subject || <em>(No subject)</em>}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{format(new Date(thread.received_at), 'PPp')}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={`${thread.processed_attachments}/${thread.total_attachments} processed${
                          thread.failed_attachments > 0 ? `, ${thread.failed_attachments} failed` : ''
                        }`}
                      >
                        <Chip
                          label={`${thread.processed_attachments}/${thread.total_attachments}`}
                          size="small"
                          color={
                            thread.processed_attachments === thread.total_attachments ? 'success' : 'default'
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={thread.status}
                        color={emailProcessorService.getThreadStatusColor(thread.status) as any}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <Collapse in={expandedRows.has(thread.id)} timeout="auto" unmountOnExit>
                        <ExpandedRow
                          thread={thread}
                          onProcess={handleProcessThread}
                          onViewAttachment={handleViewAttachment}
                          onViewThread={handleViewThread}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      {/* Conversation Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Email Thread Conversation</Typography>
            {selectedThread && (selectedThread as any).is_declaration && (
              <Chip
                label="Declaration"
                color="primary"
                icon={<EmailIcon />}
              />
            )}
          </Box>
          {selectedThread && (
            <Typography variant="caption" display="block">
              {selectedThread.subject}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedThread && (
            <Box>
              {/* Conversation Timeline */}
              {(selectedThread as any).conversation_messages && (selectedThread as any).conversation_messages.length > 0 ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Conversation Timeline
                  </Typography>
                  <Box sx={{ position: 'relative', pl: 4 }}>
                    {(selectedThread as any).conversation_messages.map((message: any, index: number) => (
                      <Box key={index} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                        {/* Timeline Line */}
                        {index < (selectedThread as any).conversation_messages.length - 1 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -20,
                              top: 40,
                              bottom: -24,
                              width: 2,
                              bgcolor: 'divider',
                            }}
                          />
                        )}

                        {/* Timeline Dot */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -30,
                            top: 12,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: message.direction === 'inbound' ? 'primary.main' : 'secondary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {message.direction === 'inbound' ?
                            <InboxIcon sx={{ fontSize: 14, color: 'white' }} /> :
                            <SendIcon sx={{ fontSize: 14, color: 'white' }} />
                          }
                        </Box>

                        {/* Message Content */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                            {format(
                              new Date(message.received_at || message.sent_at || new Date()),
                              'MMM dd, yyyy HH:mm'
                            )}
                          </Typography>

                          <Card>
                            <CardContent>
                              <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {message.direction === 'inbound'
                                    ? selectedThread.sender_name || selectedThread.sender_email
                                    : 'System'}
                                </Typography>
                                {message.action_taken && (
                                  <Chip label={message.action_taken} size="small" />
                                )}
                              </Box>
                              {message.subject && (
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                  Subject: {message.subject}
                                </Typography>
                              )}
                              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                {message.body_text || 'No message content'}
                              </Typography>
                              {message.attached_documents && message.attached_documents.length > 0 && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    {message.attached_documents.length} attachment(s)
                                  </Typography>
                                </Box>
                              )}
                              {message.extracted_fields && Object.keys(message.extracted_fields).length > 0 && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="primary">
                                    Extracted fields: {Object.keys(message.extracted_fields).join(', ')}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Email Content
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedThread.body_text || 'No content available'}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Attachments Section */}
              {selectedThread.attachments && selectedThread.attachments.length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Attachments ({selectedThread.attachments.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedThread.attachments.map((attachment) => (
                      <Grid item xs={12} sm={6} md={4} key={attachment.id}>
                        <Card>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              {(attachment as any).content_type?.includes('pdf') ? (
                                <PdfIcon color="error" />
                              ) : (
                                <DescriptionIcon color="action" />
                              )}
                              <Typography variant="subtitle2" noWrap>
                                {attachment.filename}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Size: {emailProcessorService.formatFileSize(attachment.size_bytes)}
                            </Typography>
                            {(attachment as any).document_type && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Type: {(attachment as any).document_type}
                                {(attachment as any).document_type_confidence &&
                                  ` (${(attachment as any).document_type_confidence}%)`}
                              </Typography>
                            )}
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => handleViewAttachment(selectedThread.id, attachment.id)}
                              startIcon={<VisibilityIcon />}
                            >
                              View PDF
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* ATLAS Submission Response */}
              {(selectedThread as any).atlas_submission_response && (
                <Box mt={3}>
                  <Alert severity="success">
                    <Typography variant="subtitle1" gutterBottom>
                      ATLAS Submission Successful
                    </Typography>
                    {(selectedThread as any).atlas_submission_response.reference_number && (
                      <Typography variant="body2">
                        Reference Number: {(selectedThread as any).atlas_submission_response.reference_number}
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailThreadList;
